import {
  EditOrderOffer,
  LineItem,
  MoneyBag,
  PostPurchaseOffersResponseBody,
} from '../types';
import { getRecommendedProductsBase } from '../shared/api/shared-app-requests';
import {
  APIError,
  EditOrderAPIResponse,
  IGNORE_TAGS,
  NetworkError,
} from '../shared/api/types';
import { handleResponseDeprecated, parseJson } from '../shared/api/utils';
import getLanguage from '../shared/utils/getLanguage';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { logError } from '../shared/utils/logging';
import captureException from '../utils/sentry';
import { appFunctions } from '../utils/app-utils';

async function getPostPurchaseRecommendedProducts(
  productId: number,
  limit: number,
) {
  return getRecommendedProductsBase(
    productId,
    {
      hostname: appFunctions.isBaseApp()
        ? process.env.REACT_APP_CUSTOMER_API_HOSTNAME
        : process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME,
      appPrefix: appFunctions.isBaseApp() ? 'candyrack' : 'last-upsell',
    },
    appFunctions.isBaseApp() ? IGNORE_TAGS.CR_IGNORE : IGNORE_TAGS.LU_IGNORE,
    limit,
  );
}

async function fetchOffersForCandyrackThankYouPage(body: string) {
  const hostname = process.env.REACT_APP_CUSTOMER_API_HOSTNAME;
  return fetchOffers(
    `${hostname}api/candyrack/thank-you-page/offers`,
    body,
    REGISTERED_APPS.ADD_TO_CART,
  );
}

async function fetchOffersForLastUpsell(body: string) {
  const hostname = process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME;
  return fetchOffers(
    `${hostname}api/last-upsell/popup/offers`,
    body,
    REGISTERED_APPS.THANK_YOU_PAGE,
  );
}

async function fetchOffers(
  url: string,
  body: string,
  registerApp: REGISTERED_APPS,
): Promise<PostPurchaseOffersResponseBody | null> {
  try {
    const response = await parseJson(
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'x-shop': window.Shopify.shop,
        },
        body: body,
      }),
    );
    return response;
  } catch (error) {
    logError('Error while fetching offers', registerApp, error);
    captureException(
      error instanceof Error ? error : new Error('Error while fetching offers'),
    );
  }
  return null;
}

async function getPostPurchaseOffers(
  orderId: number,
  lineItems: LineItem[],
  totalPriceSet: MoneyBag | undefined,
): Promise<PostPurchaseOffersResponseBody | null> {
  const body = JSON.stringify({
    order_id: orderId,
    line_items: lineItems,
    lang: getLanguage() || '',
    total_price_set: totalPriceSet,
  });
  if (appFunctions.isBaseApp()) {
    return (await fetchOffersForCandyrackThankYouPage(
      body,
    )) as PostPurchaseOffersResponseBody;
  } else {
    return (await fetchOffersForLastUpsell(
      body,
    )) as PostPurchaseOffersResponseBody;
  }
}

async function addToOrder(
  orderId: number,
  offers: EditOrderOffer[],
): Promise<EditOrderAPIResponse | APIError | NetworkError> {
  try {
    return await handleResponseDeprecated(
      fetch(
        appFunctions.isBaseApp()
          ? `${process.env.REACT_APP_CUSTOMER_API_HOSTNAME}api/candyrack/thank-you-page/edit-order`
          : `${process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME}api/last-upsell/popup/edit-order`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'x-shop': window.Shopify.shop,
          },
          body: JSON.stringify({
            order_id: orderId,
            offers,
          }),
        },
      ),
    );
  } catch (error) {
    let errorMessage = 'Network Related Problem';
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    return Promise.resolve({
      message: errorMessage,
      name: 'Network Error',
    });
  }
}

export {
  addToOrder,
  getPostPurchaseOffers,
  getPostPurchaseRecommendedProducts,
};
